import { TOPIC_ROLE } from '../catalog/link';
import type { TopicRole } from '../catalog/link';
import { RCU_GROUP, RELAY_VALUE } from '../catalog/protocol';
import { operateRelay, readAllRelays, readStatus } from './commands';
import type { Result } from '../result';
import type { RcuDocument } from '../wire';

/**
 * A recorded conversation, for reviewing the console without an RCU on the
 * bench.
 *
 * The replies are the command reference's own examples (branch 4.2.0, commit
 * 3986962), replayed through the same decoder the live link uses, and the
 * commands are built by the same builders the panels call. Nothing here is a
 * mock: if a builder or the decoder breaks, the demo breaks with it.
 */

export interface DemoMessage {
  readonly topicRole: TopicRole;
  readonly raw: string;
}

const ACK = 'rcuack';
const MAC = 'aa:bb:cc:dd:ee:ff';

const ack = (data: string): string =>
  `{"type":"${ACK}","mac":"${MAC}","data":${data}}`;

/** A builder's result, dropped if the demo ever asks it for something invalid. */
const built = (result: Result<RcuDocument>): readonly RcuDocument[] =>
  result.ok ? [result.value] : [];

/** Commands the demo publishes, so the feed shows both directions. */
export const demoCommands = (): readonly RcuDocument[] => [
  ...built(readStatus()),
  ...built(readAllRelays()),
  ...built(operateRelay({ group: RCU_GROUP.DEFAULT, relay: 1, value: RELAY_VALUE.ON })),
];

/** Replies the demo feeds back, newest last. */
export const demoMessages = (): readonly DemoMessage[] => [
  {
    topicRole: TOPIC_ROLE.EVENTS,
    raw: ack('{"statustype":"rcuack","groupid":3,"relayno":1,"status":"ok"}'),
  },
  {
    topicRole: TOPIC_ROLE.EVENTS,
    raw: ack(
      '{"statustype":"allrelaysstatus","relayarray":[1,0,1,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0]}',
    ),
  },
  {
    topicRole: TOPIC_ROLE.EVENTS,
    raw: ack('{"statustype":"relaystatus","groupid":3,"relayno":1,"value":1}'),
  },
  {
    topicRole: TOPIC_ROLE.EVENTS,
    raw: ack('{"statustype":"dinack","groupid":3,"dintype":"02","addr":1,"channel":1,"status":"ok"}'),
  },
  {
    topicRole: TOPIC_ROLE.EVENTS,
    raw: ack('{"statustype":"dinstatus","groupid":3,"dintype":"52","addr":1,"channel":1,"value":1}'),
  },
  {
    topicRole: TOPIC_ROLE.EVENTS,
    raw: ack('{"statustype":"daliack","master":1,"daliaddr":5,"success":true,"mode":"query","value":1}'),
  },
  {
    topicRole: TOPIC_ROLE.EVENTS,
    raw: ack('{"statustype":"listenpanel","groupid":3,"addr":1,"channel":1,"value":1}'),
  },
  {
    topicRole: TOPIC_ROLE.EVENTS,
    raw: '{"type":"status","data":{"status":"online","serialno":"888854","mac":"aa:bb:cc:dd:ee:ff","ip":"192.168.1.42","uptime":"0d 01h 12m 03s","wifi":{"ssid":"WORKSHOP","rssi":-58,"channel":6},"memory":{"internal":{"free":142000,"total":320000,"min_free":98000},"psram":{"free":2100000,"total":4194252,"min_free":1900000}},"firmware":{"version":"4.2.0","build_date":"2024-11-08"}}}',
  },
  {
    topicRole: TOPIC_ROLE.EVENTS,
    raw: ack('{"statustype":"mappingack","status":"ok"}'),
  },
  {
    topicRole: TOPIC_ROLE.EVENTS,
    raw: '{"type":"rcumappingstatus","mac":"aa:bb:cc:dd:ee:ff","data":[{"atype":"v3","x":[3,1,1,1],"a":"1:1"},{"atype":"v3","x":[3,1,2,1],"l":"3,1,1,1","d":"10,1,1,1"}]}',
  },
  {
    topicRole: TOPIC_ROLE.EVENTS,
    raw: ack('{"statustype":"loggingstatus","enabled":true,"mqtt":true}'),
  },
  {
    topicRole: TOPIC_ROLE.EVENTS,
    raw: ack('{"statustype":"coredumpinfo","present":true,"dumpid":"deadbeef","crashseq":7,"size":4096,"uploads":0,"task":"main","pc":"0x40000000","reset":"PANIC"}'),
  },
  {
    topicRole: TOPIC_ROLE.LOGS,
    raw: 'I (12345) rcu: relay 1 on\nW (12350) din: module 02 not answering\n*** 3 lines dropped ***\nI (12400) wifi: rssi -58\n',
  },
  {
    topicRole: TOPIC_ROLE.EVENTS,
    raw: ack('{"statustype":"somethingnew","status":"ok"}'),
  },
];
